type Listener = (unreachable: boolean) => void;
type WarmListener = (warm: boolean) => void;

const listeners = new Set<Listener>();
const warmListeners = new Set<WarmListener>();
let unreachable = false;
let retryTimer: ReturnType<typeof setTimeout> | null = null;
let heartbeatTimer: ReturnType<typeof setInterval> | null = null;
let expiryTimer: ReturnType<typeof setTimeout> | null = null;
let retryDelay = 3000;
let started = false;
let keepWarm = false;
let warmExpiry: number | null = null;

const KEEP_WARM_KEY = "mcc-keep-warm";
const KEEP_WARM_EXPIRY_KEY = "mcc-keep-warm-expiry";
const KEEP_WARM_DURATION_MS = 4 * 60 * 60 * 1000; // 4 hours

try {
    const storedExpiry = localStorage.getItem(KEEP_WARM_EXPIRY_KEY);
    if (storedExpiry) {
        const exp = Number(storedExpiry);
        if (exp > Date.now()) {
            keepWarm = true;
            warmExpiry = exp;
        } else {
            // Expired — clean up
            localStorage.removeItem(KEEP_WARM_KEY);
            localStorage.removeItem(KEEP_WARM_EXPIRY_KEY);
        }
    }
} catch { /* ignore */ }

const MAX_RETRY_DELAY = 30000;
const HEARTBEAT_INTERVAL = 60_000;
// Lightweight readiness probe (no auth). Avoid /v1/countries — it can contend
// with DuckDB/metadata and itself return 500 under load, falsely marking
// the whole app unreachable.
const HEALTH_ENDPOINT = "/health";

function notify() {
    listeners.forEach((fn) => fn(unreachable));
}

/** Subscribe to connection state changes. Returns unsubscribe function. */
export function onConnectionChange(fn: Listener): () => void {
    listeners.add(fn);
    if (!started) {
        started = true;
        checkHealth();
        if (keepWarm) startHeartbeat();
    }
    // Deliver current state immediately so late subscribers get it
    fn(unreachable);
    return () => listeners.delete(fn);
}

export function reportNetworkFailure() {
    if (!unreachable) {
        // Don't show overlay immediately — verify with a health check first.
        // Slow queries can fail without the server being down.
        checkHealth();
    }
}

export function reportNetworkSuccess() {
    if (unreachable) {
        unreachable = false;
        retryDelay = 3000;
        if (retryTimer) clearTimeout(retryTimer);
        retryTimer = null;
        if (keepWarm) startHeartbeat();
        notify();
    }
}

/** Toggle keep-warm heartbeat with 4h auto-expiry. Returns new state. */
export function setKeepWarm(enabled: boolean): boolean {
    keepWarm = enabled;
    clearExpiryTimer();
    if (enabled) {
        warmExpiry = Date.now() + KEEP_WARM_DURATION_MS;
        try {
            localStorage.setItem(KEEP_WARM_KEY, "true");
            localStorage.setItem(KEEP_WARM_EXPIRY_KEY, String(warmExpiry));
        } catch { /* ignore */ }
        scheduleExpiryTimer();
        if (started) startHeartbeat();
    } else {
        warmExpiry = null;
        try {
            localStorage.removeItem(KEEP_WARM_KEY);
            localStorage.removeItem(KEEP_WARM_EXPIRY_KEY);
        } catch { /* ignore */ }
        stopHeartbeat();
    }
    notifyWarmListeners();
    return keepWarm;
}

export function getKeepWarm(): boolean {
    return keepWarm;
}

/** Returns epoch ms when keep-warm expires, or null if off. */
export function getKeepWarmExpiry(): number | null {
    return keepWarm ? warmExpiry : null;
}

/** Subscribe to keep-warm state changes. Returns unsubscribe function. */
export function onKeepWarmChange(fn: WarmListener): () => void {
    warmListeners.add(fn);
    return () => warmListeners.delete(fn);
}

function notifyWarmListeners() {
    warmListeners.forEach((fn) => fn(keepWarm));
}

async function checkHealth(): Promise<boolean> {
    try {
        const resp = await fetch(HEALTH_ENDPOINT);
        if (resp.ok) {
            reportNetworkSuccess();
            return true;
        }
        // 503 warming / 500 no-instance: platform is coming up — keep probing
        // without treating as permanent disconnect on the first hit.
        if (resp.status === 503 || resp.status === 502 || resp.status === 504) {
            markUnreachable();
            return false;
        }
        if (resp.status >= 500) {
            // Other 5xx: retry a few times via scheduleHealthCheck, but mark
            // unreachable so the UI can show waiting state.
            markUnreachable();
            return false;
        }
        // 4xx = server is reachable, just returned an error
        reportNetworkSuccess();
        return true;
    } catch {
        markUnreachable();
        return false;
    }
}

function markUnreachable() {
    if (!unreachable) {
        unreachable = true;
        retryDelay = 3000;
        stopHeartbeat();
        notify();
    }
    scheduleHealthCheck();
}

/** Periodic heartbeat while connected — keeps Cloud Run instance warm. */
function startHeartbeat() {
    stopHeartbeat();
    heartbeatTimer = setInterval(checkHealth, HEARTBEAT_INTERVAL);
}

function stopHeartbeat() {
    if (heartbeatTimer) {
        clearInterval(heartbeatTimer);
        heartbeatTimer = null;
    }
}

/** Fast retry loop while disconnected — detects server coming back. */
function scheduleHealthCheck() {
    if (retryTimer) clearTimeout(retryTimer);
    retryTimer = setTimeout(async () => {
        await checkHealth();
        if (unreachable) {
            retryDelay = Math.min(retryDelay * 1.5, MAX_RETRY_DELAY);
            scheduleHealthCheck();
        }
    }, retryDelay);
}

export function isUnreachable(): boolean {
    return unreachable;
}

/** Auto-disable keep-warm after the expiry duration. */
function scheduleExpiryTimer() {
    clearExpiryTimer();
    if (!warmExpiry) return;
    const remaining = warmExpiry - Date.now();
    if (remaining <= 0) {
        setKeepWarm(false);
        return;
    }
    expiryTimer = setTimeout(() => setKeepWarm(false), remaining);
}

function clearExpiryTimer() {
    if (expiryTimer) {
        clearTimeout(expiryTimer);
        expiryTimer = null;
    }
}

// If loaded with a still-valid expiry, schedule auto-off
if (keepWarm && warmExpiry) {
    scheduleExpiryTimer();
}
